import { data, useLoaderData, useNavigate } from 'react-router'
import { BusinessCard } from '#app/components/business-card.tsx'
import { BusinessPagination } from '#app/components/business-pagination.tsx'
import { BusinessSearch } from '#app/components/business-search.tsx'
import { FilterCombobox } from '#app/components/filter-combobox.tsx'
import { Icon } from '#app/components/ui/icon.js'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildItemListLd } from '#app/seo/structured-data.ts'
import { prisma } from '#app/utils/db.server.ts'
import { pluralize } from '#app/utils/misc.tsx'
import { type Route } from './+types/$citySlug_.$categorySlug.ts'

const PAGE_SIZE = 36

export async function loader({ request, params }: Route.LoaderArgs) {
	const { citySlug, categorySlug } = params

	const url = new URL(request.url)
	const pageParam = Number(url.searchParams.get('page') || '1')
	const q = url.searchParams.get('q')?.trim() || ''
	const rawPage =
		Number.isFinite(pageParam) && pageParam > 0 ? Math.floor(pageParam) : 1

	const [city, category, subcategory, allCities, allCategories] =
		await Promise.all([
			prisma.city.findUnique({
				where: {
					slug: citySlug,
					businesses: { some: { status: 'published' } },
				},
				select: { id: true, name: true, slug: true },
			}),
			prisma.category.findUnique({
				where: {
					slug: categorySlug,
					businesses: { some: { status: 'published' } },
				},
				select: {
					id: true,
					name: true,
					slug: true,
					subcategories: {
						where: {
							businesses: { some: { status: 'published' } },
						},
						select: {
							id: true,
							name: true,
							slug: true,
						},
						orderBy: {
							name: 'asc',
						},
					},
				},
			}),
			prisma.subcategory.findUnique({
				where: {
					slug: categorySlug,
					businesses: { some: { status: 'published' } },
				},
				select: {
					id: true,
					name: true,
					slug: true,
					category: {
						select: {
							id: true,
							name: true,
							slug: true,
							subcategories: {
								where: {
									businesses: { some: { status: 'published' } },
								},
								select: {
									id: true,
									name: true,
									slug: true,
								},
								orderBy: {
									name: 'asc',
								},
							},
						},
					},
				},
			}),
			prisma.city.findMany({
				where: {
					businesses: { some: { status: 'published' } },
				},
				select: { id: true, name: true, slug: true },
				orderBy: { name: 'asc' },
			}),
			prisma.category.findMany({
				where: {
					businesses: { some: { status: 'published' } },
				},
				select: { id: true, name: true, slug: true },
				orderBy: { name: 'asc' },
			}),
		])

	if (!city || (!category && !subcategory)) {
		throw new Response('City or category not found', { status: 404 })
	}

	const effectiveCategory = subcategory ? subcategory.category : category
	const subcategories = effectiveCategory?.subcategories || []

	const andConditions: any[] = [
		{ cities: { some: { id: city.id } } },
		subcategory
			? { subcategories: { some: { id: subcategory.id } } }
			: { categories: { some: { id: effectiveCategory!.id } } },
		{ status: 'published' },
	]

	if (q.length > 0) {
		andConditions.push({
			OR: [
				{ name: { contains: q, mode: 'insensitive' as const } },
				{ description: { contains: q, mode: 'insensitive' as const } },
				{ aiDescription: { contains: q, mode: 'insensitive' as const } },
			],
		})
	}

	const where = { AND: andConditions }

	const total = await prisma.business.count({ where })

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
	const page = Math.min(rawPage, totalPages)
	const skip = (page - 1) * PAGE_SIZE

	const businesses = await prisma.business.findMany({
		where,
		select: {
			id: true,
			name: true,
			slug: true,
			description: true,
			aiDescription: true,
			phone: true,
			website: true,
			address: true,
			isFeatured: true,
			categories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			subcategories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			cities: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
		},
		orderBy: [{ isFeatured: 'desc' }, { createdAt: 'desc' }, { name: 'asc' }],
		skip,
		take: PAGE_SIZE,
	})

	return data({
		city,
		category: effectiveCategory,
		subcategory,
		businesses,
		cities: allCities,
		categories: allCategories,
		subcategories,
		page,
		pageSize: PAGE_SIZE,
		total,
	})
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	breadcrumb({
		data,
		params,
	}: {
		data: Awaited<ReturnType<typeof loader>>['data']
		params: Record<string, string>
	}) {
		const city = data?.city
		const entity = data?.subcategory ?? data?.category
		if (!city || !entity) return null
		return {
			name: `${pluralize(entity.name)} in ${city.name}`,
			url: `/businesses/${params.citySlug}/${params.categorySlug}`,
		}
	},
	seo({
		loaderData,
		origin,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
	}): SeoProps | null {
		const city = loaderData?.city
		const entity = loaderData?.subcategory ?? loaderData?.category
		if (!city || !entity) return null
		const canonical = `${origin}/businesses/${city.slug}/${entity.slug}`

		const structuredData =
			loaderData.businesses.length > 0
				? [
						buildItemListLd({
							name: `${pluralize(entity.name)} in ${city.name}`,
							items: loaderData.businesses.map((b) => ({
								name: b.name,
								url: `${origin}/businesses/${b.slug}`,
							})),
						}),
					]
				: undefined

		return {
			title: `${pluralize(entity.name)} in ${city.name} - RentDFW.com`,
			description: `Browse ${pluralize(entity.name.toLowerCase())} businesses in ${city.name}. Find professional services available today.`,
			canonical,
			openGraph: {
				title: `${pluralize(entity.name)} in ${city.name} - RentDFW.com`,
				description: `Browse ${pluralize(entity.name.toLowerCase())} businesses in ${city.name}.`,
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: `${pluralize(entity.name)} in ${city.name} - RentDFW.com`,
				description: `Browse ${pluralize(entity.name.toLowerCase())} businesses in ${city.name}.`,
			},
			structuredData,
		}
	},
}

export default function BusinessesCityCategoryPage() {
	const {
		city,
		category,
		subcategory,
		businesses,
		cities,
		categories,
		subcategories,
		page,
		pageSize,
		total,
	} = useLoaderData<typeof loader>()
	const navigate = useNavigate()

	if (!category) {
		throw new Error('Category is required')
	}

	const entity = subcategory ?? category

	const currentSubcategoryValue = subcategory ? subcategory.slug : 'all'
	const totalPages = Math.max(1, Math.ceil(total / pageSize))

	function handleCityChange(value: string) {
		if (!category) return
		const categorySlug = subcategory ? subcategory.slug : category.slug
		void navigate(`/businesses/${value}/${categorySlug}`)
	}

	function handleCategoryChange(value: string) {
		void navigate(`/businesses/${city.slug}/${value}`)
	}

	function handleSubcategoryChange(value: string) {
		if (!category) return

		if (value === 'all') {
			void navigate(`/businesses/${city.slug}/${category.slug}`)
		} else {
			void navigate(`/businesses/${city.slug}/${value}`)
		}
	}

	return (
		<>
			{/* SEO is handled by SeoHead via handle.seo */}
			<div className="container mx-auto px-4 py-16">
				<div className="mb-8">
					<div className="mb-4 flex flex-col gap-4">
						<div className="mx-auto max-w-3xl text-center">
							<h1 className="text-4xl font-semibold text-balance">
								{city.name} {entity.name} Rental Businesses
							</h1>
							<p className="text-muted-foreground mt-2 mb-8 text-xl">
								Looking for {entity.name.toLowerCase()} rental businesses in{' '}
								{city.name}? Compare services and connect with local providers
								nearby.
							</p>
						</div>

						<div className="flex flex-col gap-3 md:flex-row">
							<div className="w-full">
								<BusinessSearch total={total} />
							</div>
							{cities.length > 0 && (
								<div className="w-full md:w-64">
									<FilterCombobox
										options={cities.map((c) => ({
											label: c.name,
											value: c.slug,
										}))}
										value={city.slug}
										onValueChange={handleCityChange}
										placeholder="Filter by city"
										searchPlaceholder="Search cities..."
										emptyText="No cities found"
									/>
								</div>
							)}
							{categories.length > 0 && (
								<div className="w-full md:w-64">
									<FilterCombobox
										options={categories.map((cat) => ({
											label: cat.name,
											value: cat.slug,
										}))}
										value={category.slug}
										onValueChange={handleCategoryChange}
										placeholder="Filter by category"
										searchPlaceholder="Search categories..."
										emptyText="No categories found"
									/>
								</div>
							)}
							{subcategories.length > 0 && (
								<div className="w-full md:w-64">
									<FilterCombobox
										options={[
											{
												label: `All ${pluralize(category.name)}`,
												value: 'all',
											},
											...subcategories.map((sub) => ({
												label: sub.name,
												value: sub.slug,
											})),
										]}
										value={currentSubcategoryValue}
										onValueChange={handleSubcategoryChange}
										placeholder="Filter by subcategory"
										searchPlaceholder="Search subcategories..."
										emptyText="No subcategories found"
									/>
								</div>
							)}
						</div>
					</div>
				</div>

				{total === 0 ? (
					<div className="bg-muted rounded-lg py-16 text-center">
						<Icon
							name="file-text"
							className="text-muted-foreground mx-auto mb-4 size-12"
						/>
						<h3 className="mb-2 text-xl font-semibold">No Businesses Found</h3>
						<p className="text-muted-foreground">
							No {entity.name.toLowerCase()} businesses in {city.name} at the
							moment.
						</p>
					</div>
				) : (
					<>
						<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
							{businesses.map((business) => (
								<BusinessCard key={business.id} business={business} />
							))}
						</div>

						<BusinessPagination currentPage={page} totalPages={totalPages} />
					</>
				)}
			</div>
		</>
	)
}
